import Cookies from 'js-cookie';

// Cookie configuration for security
const COOKIE_CONFIG = {
  // Basic security settings
  secure: process.env.NODE_ENV === 'production', // Only HTTPS in production
  sameSite: 'strict', // CSRF protection
  path: '/', // Available throughout the app
  
  // Token-specific settings
  token: {
    expires: 7, // 7 days for auth token
    httpOnly: false, // Must be false for client-side access (JS-accessible)
  },
  
  // User data settings
  user: {
    expires: 7, // 7 days for user data
    httpOnly: false, // Must be false for client-side access
  },
  
  // Refresh token settings (more secure)
  refreshToken: {
    expires: 30, // 30 days for refresh token
    httpOnly: false, // Would be true in ideal setup with backend support
  }
};

// Cookie names (with app prefix for uniqueness)
const COOKIE_NAMES = {
  TOKEN: 'hyrex_auth_token',
  USER: 'hyrex_user_data',
  REFRESH_TOKEN: 'hyrex_refresh_token',
  REMEMBER_ME: 'hyrex_remember_me',
  LAST_ACTIVITY: 'hyrex_last_activity',
  CURRENT_CLIENT: 'hyrex_current_client'
};

/**
 * Set authentication token in secure cookie
 * @param {string} token - The authentication token
 * @param {boolean} rememberMe - Whether to persist across browser sessions
 */
export const setAuthToken = (token, rememberMe = false) => {
  // Validate token is a string
  if (!token || typeof token !== 'string') {
    console.error('❌ Invalid token for cookie storage:', { token, type: typeof token });
    throw new Error('Token must be a non-empty string');
  }
  
  const config = {
    secure: COOKIE_CONFIG.secure,
    sameSite: COOKIE_CONFIG.sameSite,
    path: COOKIE_CONFIG.path,
    expires: rememberMe ? 30 : 7, // Longer expiry if "remember me"
  };
  
  console.log('🍪 Setting auth token cookie:', { tokenLength: token.length, rememberMe, config });
  Cookies.set(COOKIE_NAMES.TOKEN, token, config);
  
  // Store remember preference
  const rememberConfig = {
    secure: COOKIE_CONFIG.secure,
    sameSite: COOKIE_CONFIG.sameSite,
    path: COOKIE_CONFIG.path,
    expires: 365 // Remember preference for a year
  };
  
  Cookies.set(COOKIE_NAMES.REMEMBER_ME, rememberMe.toString(), rememberConfig);
  
  // Update last activity
  updateLastActivity();
  
  console.log('🍪 Auth token stored in secure cookie', { rememberMe, expires: config.expires });
};

/**
 * Get authentication token from cookie
 * @returns {string|null} The token or null if not found
 */
export const getAuthToken = () => {
  const token = Cookies.get(COOKIE_NAMES.TOKEN);
  
  if (token) {
    // Update last activity when token is accessed
    updateLastActivity();
  }
  
  return token || null;
};

/**
 * Set user data in secure cookie
 * @param {object} user - The user object
 * @param {boolean} rememberMe - Whether to persist across browser sessions
 */
export const setAuthUser = (user, rememberMe = false) => {
  // Validate user is an object
  if (!user || typeof user !== 'object') {
    console.error('❌ Invalid user data for cookie storage:', { user, type: typeof user });
    throw new Error('User must be a valid object');
  }
  
  const config = {
    secure: COOKIE_CONFIG.secure,
    sameSite: COOKIE_CONFIG.sameSite,
    path: COOKIE_CONFIG.path,
    expires: rememberMe ? 30 : 7,
  };
  
  console.log('🍪 Setting user data cookie:', { userId: user?.id || user?.user_id, rememberMe, config });
  
  // Stringify user data for cookie storage
  const userData = JSON.stringify(user);
  Cookies.set(COOKIE_NAMES.USER, userData, config);
  
  console.log('🍪 User data stored in secure cookie', { userId: user?.id || user?.user_id, rememberMe });
};

/**
 * Get user data from cookie
 * @returns {object|null} The user object or null if not found
 */
export const getAuthUser = () => {
  const userData = Cookies.get(COOKIE_NAMES.USER);
  
  if (userData) {
    try {
      return JSON.parse(userData);
    } catch (error) {
      console.error('❌ Failed to parse user data from cookie:', error);
      // Remove corrupted data
      removeAuthUser();
      return null;
    }
  }
  
  return null;
};

/**
 * Set refresh token in secure cookie
 * @param {string} refreshToken - The refresh token
 * @param {boolean} rememberMe - Whether to persist across browser sessions
 */
export const setRefreshToken = (refreshToken, rememberMe = false) => {
  const config = {
    ...COOKIE_CONFIG,
    ...COOKIE_CONFIG.refreshToken,
    expires: rememberMe ? 90 : 30, // Longer expiry for refresh tokens
  };
  
  Cookies.set(COOKIE_NAMES.REFRESH_TOKEN, refreshToken, config);
  console.log('🍪 Refresh token stored in secure cookie');
};

/**
 * Get refresh token from cookie
 * @returns {string|null} The refresh token or null if not found
 */
export const getRefreshToken = () => {
  return Cookies.get(COOKIE_NAMES.REFRESH_TOKEN) || null;
};

/**
 * Update last activity timestamp
 */
export const updateLastActivity = () => {
  const timestamp = new Date().toISOString();
  const config = {
    secure: COOKIE_CONFIG.secure,
    sameSite: COOKIE_CONFIG.sameSite,
    path: COOKIE_CONFIG.path,
    expires: 1 // 1 day for activity tracking
  };
  
  Cookies.set(COOKIE_NAMES.LAST_ACTIVITY, timestamp, config);
  console.log('🍪 Last activity updated:', timestamp);
};

/**
 * Get last activity timestamp
 * @returns {string|null} ISO timestamp or null
 */
export const getLastActivity = () => {
  return Cookies.get(COOKIE_NAMES.LAST_ACTIVITY) || null;
};

/**
 * Check if "remember me" was selected
 * @returns {boolean} Whether remember me is enabled
 */
export const getRememberMe = () => {
  const rememberMe = Cookies.get(COOKIE_NAMES.REMEMBER_ME);
  return rememberMe === 'true';
};

/**
 * Get remember me preference (alias for getRememberMe)
 * @returns {boolean} True if remember me is enabled
 */
export const getRememberMePreference = () => {
  return getRememberMe();
};

/**
 * Remove authentication token
 */
export const removeAuthToken = () => {
  Cookies.remove(COOKIE_NAMES.TOKEN, { path: '/' });
  console.log('🍪 Auth token removed from cookie');
};

/**
 * Remove user data
 */
export const removeAuthUser = () => {
  Cookies.remove(COOKIE_NAMES.USER, { path: '/' });
  console.log('🍪 User data removed from cookie');
};

/**
 * Remove refresh token
 */
export const removeRefreshToken = () => {
  Cookies.remove(COOKIE_NAMES.REFRESH_TOKEN, { path: '/' });
  console.log('🍪 Refresh token removed from cookie');
};

/**
 * Clear all authentication-related cookies
 */
export const clearAllAuthCookies = () => {
  removeAuthToken();
  removeAuthUser();
  removeRefreshToken();
  Cookies.remove(COOKIE_NAMES.LAST_ACTIVITY, { path: '/' });
  // Keep remember me preference
  console.log('🍪 All auth cookies cleared');
};

/**
 * Clear authentication data (alias for clearAllAuthCookies)
 */
export const clearAuth = () => {
  clearAllAuthCookies();
};

/**
 * Clear auth cookies (alias for clearAllAuthCookies)
 */
export const clearAuthCookies = () => {
  clearAllAuthCookies();
};

/**
 * Check if user is authenticated based on cookie presence
 * @returns {boolean} Whether user appears to be authenticated
 */
export const isAuthenticated = () => {
  const token = getAuthToken();
  const user = getAuthUser();
  return !!(token && user);
};

/**
 * Check if authentication is expired based on last activity
 * @param {number} maxInactiveHours - Maximum hours of inactivity allowed
 * @returns {boolean} Whether authentication has expired
 */
export const isAuthExpired = (maxInactiveHours = 24) => {
  const lastActivity = getLastActivity();
  
  if (!lastActivity) {
    return true; // No activity recorded, consider expired
  }
  
  const lastActivityDate = new Date(lastActivity);
  const now = new Date();
  const hoursSinceActivity = (now - lastActivityDate) / (1000 * 60 * 60);
  
  return hoursSinceActivity > maxInactiveHours;
};

/**
 * Migrate data from localStorage/sessionStorage to cookies
 * This helps with the transition from old storage to new cookie system
 */
export const migrateFromLocalStorage = () => {
  console.log('🔄 Migrating authentication data from localStorage/sessionStorage to cookies...');
  
  // Check both localStorage and sessionStorage
  const storages = [localStorage, sessionStorage];
  let migrated = false;
  
  for (const storage of storages) {
    const token = storage.getItem('hyrex_token');
    const user = storage.getItem('hyrex_user');
    
    if (token && user) {
      try {
        const parsedUser = JSON.parse(user);
        const wasLocalStorage = storage === localStorage;
        
        // Migrate to cookies
        setAuthToken(token, wasLocalStorage); // localStorage usage suggests "remember me"
        setAuthUser(parsedUser, wasLocalStorage);
        
        // Clear old storage
        storage.removeItem('hyrex_token');
        storage.removeItem('hyrex_user');
        
        migrated = true;
        console.log('✅ Successfully migrated auth data to cookies', { 
          source: wasLocalStorage ? 'localStorage' : 'sessionStorage',
          rememberMe: wasLocalStorage 
        });
        
        break; // Use first valid data found
      } catch (error) {
        console.error('❌ Failed to migrate auth data:', error);
        // Clean up corrupted data
        storage.removeItem('hyrex_token');
        storage.removeItem('hyrex_user');
      }
    }
  }
  
  if (!migrated) {
    console.log('ℹ️ No auth data found to migrate');
  }
  
  return migrated;
};

// Export cookie names for external use if needed
export { COOKIE_NAMES };

// Auto-migrate on import (runs once when module is loaded)
if (typeof window !== 'undefined') {
  // Only run in browser environment
  migrateFromLocalStorage();
}

/**
 * Client Context Cookie Functions
 * Store client information in secure cookies instead of localStorage
 */

/**
 * Set current client in cookie
 * @param {object} client - The client object to store
 */
export const setCurrentClient = (client) => {
  if (!client || typeof client !== 'object') {
    console.error('❌ Invalid client data for cookie storage:', { client, type: typeof client });
    throw new Error('Client must be a valid object');
  }
  
  const config = {
    secure: COOKIE_CONFIG.secure,
    sameSite: COOKIE_CONFIG.sameSite,
    path: COOKIE_CONFIG.path,
    expires: 7, // 7 days for client context
  };
  
  const clientString = JSON.stringify(client);
  console.log('🍪 Setting current client cookie:', { clientId: client.id, clientName: client.name });
  Cookies.set(COOKIE_NAMES.CURRENT_CLIENT, clientString, config);
};

/**
 * Get current client from cookie
 * @returns {object|null} The client object or null if not found
 */
export const getCurrentClient = () => {
  const clientString = Cookies.get(COOKIE_NAMES.CURRENT_CLIENT);
  
  if (!clientString) {
    return null;
  }
  
  try {
    const client = JSON.parse(clientString);
    console.log('🍪 Retrieved current client from cookie:', { clientId: client.id, clientName: client.name });
    return client;
  } catch (error) {
    console.error('❌ Failed to parse client cookie:', error);
    // Remove corrupted cookie
    removeCurrentClient();
    return null;
  }
};

/**
 * Remove current client cookie
 */
export const removeCurrentClient = () => {
  console.log('🍪 Removing current client cookie');
  Cookies.remove(COOKIE_NAMES.CURRENT_CLIENT, { path: '/' });
};

/**
 * Clear current client (alias for removeCurrentClient)
 */
export const clearCurrentClient = () => {
  removeCurrentClient();
};

/**
 * Check if current client exists
 * @returns {boolean} True if current client exists
 */
export const hasCurrentClient = () => {
  return !!getCurrentClient();
};

/**
 * Clear all client-related cookies
 */
export const clearAllClientCookies = () => {
  removeCurrentClient();
  console.log('🍪 All client cookies cleared');
};

/**
 * Clear all cookies (auth + client)
 */
export const clearAllCookies = () => {
  clearAllAuthCookies();
  clearAllClientCookies();
  
  // Clear any additional cookies that might exist
  const allCookies = Cookies.get();
  Object.keys(allCookies).forEach(cookieName => {
    if (cookieName.startsWith('hyrex_') || cookieName.includes('auth') || cookieName.includes('client')) {
      Cookies.remove(cookieName, { path: '/' });
      console.log(`🍪 Removed additional cookie: ${cookieName}`);
    }
  });
  
  // Clear localStorage items that might contain sensitive data
  if (typeof window !== 'undefined' && window.localStorage) {
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (key.startsWith('hyrex_') || key.includes('auth') || key.includes('user') || key.includes('token'))) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => {
      localStorage.removeItem(key);
      console.log(`🗑️ Removed localStorage item: ${key}`);
    });
  }
  
  // Clear sessionStorage items
  if (typeof window !== 'undefined' && window.sessionStorage) {
    const keysToRemove = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key && (key.startsWith('hyrex_') || key.includes('auth') || key.includes('user') || key.includes('token'))) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => {
      sessionStorage.removeItem(key);
      console.log(`🗑️ Removed sessionStorage item: ${key}`);
    });
  }
  
  console.log('🍪 All cookies, localStorage, and sessionStorage cleared');
};
